import React, { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetEngagementSegmentsQuery } from "../../../Data/services/engagementApi";
import { ErrorToast } from "../../../Hooks/useToast";
import {
  ENGAGEMENT_SEGMENT_KEYS,
  SUBSCRIPTION_SEGMENT_KEYS,
  segmentLabel,
} from "../constants/labels";
import styles from "../styles/Dashboard.module.scss";
import {
  BAR_COLOR,
  PIE_COLORS,
  extractData,
  normalizeSegments,
} from "../utils";
import ChartSkeleton from "./ChartSkeleton";

export default function SegmentChartsSection({ onSegmentSelect }) {
  const {
    data: segmentsRes,
    isLoading,
    isError,
    error,
  } = useGetEngagementSegmentsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isError) {
      ErrorToast(
        error?.data?.message || "Failed to load engagement segments"
      );
    }
  }, [isError, error]);

  const segments = normalizeSegments(extractData(segmentsRes));

  const subscriptionPieData = useMemo(
    () =>
      SUBSCRIPTION_SEGMENT_KEYS.map((key) => ({
        key,
        name: segmentLabel(key),
        value: Number(segments[key]) || 0,
      })),
    [segments]
  );

  const engagementBarData = useMemo(
    () =>
      ENGAGEMENT_SEGMENT_KEYS.map((key) => ({
        key,
        name: segmentLabel(key),
        short:
          key === "low_engagement"
            ? "Quiet"
            : key === "inactive_14"
            ? "≥14d"
            : key === "inactive_30"
            ? "≥30d"
            : key === "inactive"
            ? "7–30d"
            : "Active",
        value: Number(segments[key]) || 0,
      })),
    [segments]
  );

  return (
    <Row className="w-100 mt-4 g-3">
      <Col lg={6} md={12}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h5 className={styles.panelTitle}>Subscription mix</h5>
            <p className={styles.panelHint}>Free · Premium · Expired</p>
          </div>
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <div className={styles.chartBox}>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={subscriptionPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={95}
                    paddingAngle={2}
                    onClick={(entry) =>
                      onSegmentSelect?.(entry?.key || entry?.payload?.key)
                    }
                  >
                    {subscriptionPieData.map((entry, index) => (
                      <Cell
                        key={entry.key}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                        cursor="pointer"
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className={styles.chipRow}>
            {SUBSCRIPTION_SEGMENT_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={styles.chip}
                onClick={() => onSegmentSelect?.(key)}
              >
                {segmentLabel(key)} · {Number(segments[key]) || 0}
              </button>
            ))}
          </div>
        </div>
      </Col>

      <Col lg={6} md={12}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h5 className={styles.panelTitle}>Engagement health</h5>
            <p className={styles.panelHint}>Active vs inactive cohorts</p>
          </div>
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <div className={styles.chartBox}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  layout="vertical"
                  data={engagementBarData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="short"
                    width={64}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={(value, _n, props) => [
                      value,
                      props?.payload?.name,
                    ]}
                  />
                  <Bar
                    dataKey="value"
                    fill={BAR_COLOR}
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(entry) =>
                      onSegmentSelect?.(entry?.key || entry?.payload?.key)
                    }
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className={styles.chipRow}>
            {ENGAGEMENT_SEGMENT_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={styles.chip}
                onClick={() => onSegmentSelect?.(key)}
              >
                {segmentLabel(key)} · {Number(segments[key]) || 0}
              </button>
            ))}
          </div>
        </div>
      </Col>
    </Row>
  );
}
